import keys from 'client/components/txtkeys/payNowKeys'
import { PayNowInterface } from 'client/Interfaces/PayNow/AccountAuthorizationParams'
import { SettingsInterface } from 'client/Interfaces/Settings/SettingsInterface'
import { l } from 'client/Other/Translation'
import React from 'react'

import { PaymentMeans as methods } from '../../../Interfaces/Settings/PaymentMeanInterface'
import { ButtonType } from '../../common/Button/Button'
import { isIBANValid } from './helper'

const InputGroupHeader = React.lazy(() => import('../../common/Input/InputGroupHeader'))
const IBANFields = React.lazy(() => import('./IBANFields'))
const Input = React.lazy(() => import('../../common/Input/Input'))
const Button = React.lazy(() => import('../../common/Button/Button'))
const CheckBox = React.lazy(() => import('../../common/CheckBox/CheckBox'))

interface PropsType {
  values: any[]
  onChange: (values, changedFields) => void
  locale: string
  schema: any
  onOpenActivatePayNowModal: () => void
  payNow: PayNowInterface
  togglePayNowStatus: (status: boolean) => void
  getIbans?: (ibans) => void
  getSwifts?: (swifts) => void
  stateSettings: SettingsInterface
}

const PaymentDetailsForm: React.FC<PropsType> = (props) => {
  const [values, setValues] = React.useState<any[]>(props.values)
  const [changedFields, setChangedFields] = React.useState<any>({})

  const valueFor = (method, field) => {
    const methodVals = values.find((f) => f.type === method)
    return methodVals && methodVals.fields ? methodVals.fields[field] : ''
  }

  const onChange = (method, field, value) => {
    const newValues = [...values]
    let methodVals = newValues.find((f) => f.type === method)
    if (!methodVals) {
      methodVals = { type: method }
      newValues.push(methodVals)
    }
    if (!methodVals.fields) {
      methodVals.fields = {}
    }
    methodVals.fields[field] = value

    setValues(newValues)
    setChangedFields({ ...changedFields, [`${method}.${field}`]: true })
    props.onChange(newValues, Object.keys(changedFields))
  }

  const renderInputField = (f, paymentMethod) => {
    return (
      !f.hidden && (
        <Input
          key={f.name}
          label={l(f.label)}
          value={valueFor(paymentMethod, f.name)}
          automation={f.name}
          onChange={(val) => onChange(paymentMethod, f.name, val)}
        />
      )
    )
  }

  const renderPaypalFields = (fields) => {
    return <div>{fields.map((f) => renderInputField(f, methods.PAYPAL))} </div>
  }

  const renderSwishFields = (fields) => {
    return <div>{fields.map((f) => renderInputField(f, methods.SWISH))}</div>
  }

  const renderPlusgiroFields = (fields) => {
    return <div> {fields.map((f) => renderInputField(f, methods.PLUSGIRO))}</div>
  }

  const renderBankgiroFields = (fields) => {
    return <div> {fields.map((f) => renderInputField(f, methods.BANKGIRO))}</div>
  }

  const renderBankTransferFields = (fields) => {
    return <div>{fields.map((f) => renderInputField(f, methods.BBAN))}</div>
  }
  const renderOtherFields = (fields) => {
    return <div>{fields.map((f) => renderInputField(f, methods.OTHER))}</div>
  }

  const renderOnlinePaymentConnectOption = (onOpenActivatePayNowModal, hasActivePayNowAccount, togglePayNowStatus) => {
    return (
      <div className="OnlinePayment">
        <div className="OnlinePayment__label">
          {l('PaymentMethod.onlinePayment.label')}
          <div className="OnlinePayment__icons__cards" />
        </div>
        {hasActivePayNowAccount ? (
          <div className={'OnlinePayment__settings'}>
            <CheckBox
              label={l(keys.acceptCards)}
              value={props.payNow.status.active}
              automation="OnlinePayment__settings__acceptCards"
              onChange={(status) => {
                togglePayNowStatus(status)
              }}
            />
            <div className="OnlinePayment__settings__subLabel">{l(keys.acceptCardsSubLabel)}</div>
          </div>
        ) : (
          <div className="OnlinePayment__info">
            <div className="OnlinePayment__info__body">{l('PaymentMethod.onlinePayment.info')}</div>
            <Button
              label={l('PaymentMethod.onlinePayment.connect')}
              className="OnlinePayment__connectButton"
              type={ButtonType.link}
              action={onOpenActivatePayNowModal}
            />
          </div>
        )}
      </div>
    )
  }

  const getIbans = () => {
    const ibanValidationResultArray = []
    const ibanArray = []

    // First record is not always IBAN
    const ibanValue = values.filter((value) => value.type === 'IBAN')

    if (ibanValue.length > 0) {
      ibanArray.push(ibanValue[0].fields.iban)
      if ('optionalIBANS' in ibanValue[0]) {
        ibanValue[0].optionalIBANs.map((opt) => ibanArray.push(opt.iban))
      }
      ibanArray.map((iban) => {
        const result = isIBANValid(iban)
        ibanValidationResultArray.push(result)
      })
    }
    const result = !ibanValidationResultArray.includes(false)
    props?.getIbans(result)
  }

  const getSwifts = () => {
    const swiftValidationResultArray = []
    const swiftArray = []

    const swiftValue = values.filter((value) => value.type === 'IBAN')

    if (swiftValue.length > 0) {
      swiftArray.push(swiftValue[0].fields.swift)
      if ('optionalIBANs' in swiftValue[0]) {
        swiftValue[0].optionalIBANs.map((opt) => swiftArray.push(opt.swift))
      }
      swiftArray.map((swift) => {
        const result = (swift?.length >= 8 && swift?.length <= 11) || swift?.length === 0
        swiftValidationResultArray.push(result)
      })
    }
    const result = !swiftValidationResultArray.includes(false)
    props?.getSwifts(result)
  }

  const { paypal, swish, plusgiro, bankgiro, iban, bban, other } = props.schema
  const isMethodEnabled = (pm) => pm && pm.enabled
  const hasData = (pmSchema) => {
    const { type, fields } = pmSchema
    return fields.some((field) => valueFor(type, field) !== '')
  }
  const isShowPM = (pm) => isMethodEnabled(pm) || hasData(pm)
  const hasActivePayNowAccount = props.payNow && Boolean(props.payNow.status.connected)

  return (
    <div className="companyInformationModal__form-wrapper">
      {isShowPM(iban) && (
        <IBANFields
          schema={iban.fields}
          values={values.find((f) => f.type === methods.IBAN)}
          locale={props.locale}
          stateSettings={props.stateSettings}
          onChange={(IBANVals, changedField: string) => {
            const newValues = [...values]
            const IBANIndex = newValues.findIndex((f) => f.type === methods.IBAN)
            if (IBANIndex >= 0) {
              newValues[IBANIndex] = IBANVals
            } else {
              newValues.push(IBANVals)
            }

            setValues(newValues)
            setChangedFields({ ...changedFields, [`IBAN.${changedField}`]: true })
            props.onChange(newValues, Object.keys(changedFields))
          }}
        />
      )}

      {getIbans()}
      {getSwifts()}

      {renderOnlinePaymentConnectOption(
        props.onOpenActivatePayNowModal,
        hasActivePayNowAccount,
        props.togglePayNowStatus,
      )}

      {isShowPM(bban) && <InputGroupHeader title={l('settings.paymentMethod.BBAN')} />}
      {isShowPM(bban) && renderBankTransferFields(bban.fields)}

      {isShowPM(swish) && <InputGroupHeader title={l('settings.paymentMethod.SWISH')} />}
      {isShowPM(swish) && renderSwishFields(swish.fields)}

      {isShowPM(plusgiro) && <InputGroupHeader title={l('settings.paymentMethod.PLUSGIRO')} />}
      {isShowPM(plusgiro) && renderPlusgiroFields(plusgiro.fields)}

      {isShowPM(bankgiro) && <InputGroupHeader title={l('settings.paymentMethod.BANKGIRO')} />}
      {isShowPM(bankgiro) && renderBankgiroFields(bankgiro.fields)}

      {isShowPM(paypal) && <InputGroupHeader title={l('settings.paymentMethod.PAYPAL')} />}
      {isShowPM(paypal) && renderPaypalFields(paypal.fields)}

      {isShowPM(other) && <InputGroupHeader title={l('settings.paymentMethod.OTHER')} />}
      {isShowPM(other) && renderOtherFields(other.fields)}
    </div>
  )
}

export default PaymentDetailsForm
